// The playground's design-system thesis. A component gets its look in exactly
// one of three legitimate ways:
//
//   pipe    - use raw MUI; the theme already styles it. Nothing to add.
//   wrapped - a reusable component owns the styling. Consume it, don't restyle.
//   theme   - a global theme/token change. Deliberate, rare, never per-feature
//             (it lands on every instance everywhere). A theme change is only a
//             *pending* thing: once it ships to prod it is simply the theme, and
//             the component goes back to being pipe-through. So a theme change
//             has a status:
//               proposed - prototype theme only (designs/theme.tsx), not on prod.
//                          The gate: shipping it hits every instance everywhere.
//               shipped  - already a deliberate global token on prod. Not a
//                          warning, the accepted default; shown for provenance.
//
// sx is for one-offs, the theme is for anything repeated. MUI 9 removed system
// props, so layout sx (padding, gap, flex, width) is always fine. Visual sx
// (colour, border, radius, type) is fine on a feature component using theme
// values, never to restyle a llama component, and moves into the theme or a
// wrapped component once it repeats.
// Structure (what / props / made-of) is read live from the React tree; only the
// value-add lives here.

export type DsApproach = 'pipe' | 'wrapped' | 'theme';

export type ThemeChange = {
  status: 'proposed' | 'shipped';
  note: string; // what the change is + why it matters
};

// A single changelog entry. `status` tracks whether the change is merged to main
// or still pending (only in the prototype / awaiting a PR). Flip the status here
// and both the per-component Spec and the aggregate Changelog update together.
export type ChangeStatus = 'pending' | 'merged';

export type ChangeEntry = { date: string; note: string; status: ChangeStatus };

export type DsEntry = {
  approach: DsApproach;
  tokens: string[]; // the theme tokens it relies on (pipe/theme) or that the wrapper owns
  themeChange?: ThemeChange; // approach 'theme': the global change + its prod status
  rationale?: string;
  changelog?: ChangeEntry[];
  file?: string; // where a wrapped component lives, for navigation
  // A route in the prototype where this component renders live, so the
  // changelog can deep-link to "see it in action". The page auto-selects the
  // component (`select`, defaults to the registry key) on arrival.
  demo?: { route: string; select?: string };
};

export const REGISTRY: Record<string, DsEntry> = {
  // ── Pipe through MUI: raw element, look comes entirely from the theme ──────
  Card: {
    demo: { route: '/karehero-app/home' },
    approach: 'wrapped',
    tokens: [
      'surface: elevation 0 · neutral.50 · 1px neutral.200 · radius.md',
      'dark: charcoal or color.800 fill, neutral.50 text',
      'variant: default | compact (size)',
      'chevron-right Icon in the CardHeader action slot (ml 2, vertically centered; inherits colour, neutral.50 on dark) — shown when onClick is set, or chevron is forced',
    ],
    rationale:
      'The consolidated card. Plain, dark and navigational cards are one component: onClick makes it navigational, which brings llama\u2019s listItem press state and a chevron. The squiggle is gone — it was opted into by one Storybook control and nothing else, and the real squiggles were llama SquiggleCards on Home tinted from three colours that never matched the Figma. Replaced NavCard; ActionCard stays its own component.',
    changelog: [
      {
        date: '2026-06',
        note: 'Consolidated card built per the Notion plan; row cards (calls, forms, tools, shortlists) migrated onto it',
        status: 'pending',
      },
      {
        date: '2026-05',
        note: 'Surface themed in llama: elevation 0, neutral.200 border, radius md.',
        status: 'merged',
      },
    ],
    file: 'front-end/designs/src/components/wrapped/Card.tsx',
    // The consolidated prototype Card folds SquiggleCard/NavCard back into one
    // component. It forks llama's atom at
    // lib/llama/src/components/atoms/Card/Card.tsx, which is the file to
    // reconcile against when this lands.
  },
  TextField: {
    demo: { route: '/karehero-app/onboarding' },
    approach: 'pipe',
    tokens: [
      'variant filled',
      'bg neutral.50',
      '1px neutral.500',
      'radius 4',
      'focus: marine border + inset ring',
      'label shrink',
    ],
    themeChange: {
      status: 'shipped',
      note: 'The filled variant + marine focus ring is a KareHero prod theme customisation, not vanilla MUI (which defaults to outlined). Already global and accepted - just use <TextField>.',
    },
    changelog: [
      {
        date: '2026-05',
        note: 'Filled variant + marine focus ring themed in llama.',
        status: 'merged',
      },
    ],
  },
  Select: {
    approach: 'pipe',
    tokens: [
      'filled variant + size flow from the FormControl',
      'native MUI dropdown caret',
    ],
    rationale:
      'Pipe-through MUI Select; the theme styles it and it brings its own dropdown caret. Use <Select> inside a <FormControl> so the filled variant and size flow down. No per-instance sx.',
  },
  Checkbox: { approach: 'pipe', tokens: ['checked: marine (color primary)'] },
  Typography: {
    approach: 'pipe',
    tokens: [
      'h1-h3: Lora SemiBold Italic',
      'h4+ / body / caption: Work Sans',
      'overline: uppercase',
    ],
    rationale: 'Pick the variant. Never set font or size by hand.',
  },
  PageHeader: {
    approach: 'wrapped',
    tokens: [
      'title: Typography (h1 default; h2-h6 via variant)',
      'description (optional): Typography subtitle1',
      'layout: Stack column, spacing=1',
      'align: left default; centered via isCentered',
    ],
    rationale:
      'The llama page/section header. Consume it for every screen title instead of an inline Typography + margin, so titles stay consistent. Props: title, description, variant, isCentered.',
    file: 'lib/llama/src/pwa/atoms/PageHeader/PageHeader.tsx',
  },
  Avatar: {
    approach: 'pipe',
    tokens: ['default bg limeGreen.500 / marine text'],
    changelog: [
      {
        date: '2026-05',
        note: 'Default limeGreen bg / marine text themed in llama.',
        status: 'merged',
      },
    ],
  },
  Toolbar: { approach: 'pipe', tokens: [] },
  Paper: { approach: 'pipe', tokens: ['elevation 0'] },
  Divider: { approach: 'pipe', tokens: ['line = divider (neutral.500)'] },
  Link: { approach: 'pipe', tokens: [] },
  IconButton: { approach: 'pipe', tokens: [] },
  MenuList: { approach: 'pipe', tokens: [] },
  MenuItem: { approach: 'pipe', tokens: [] },
  ToggleButton: {
    approach: 'theme',
    tokens: [
      'pill, no border, textTransform none',
      'idle: text.primary on the group track',
      'selected: coral.700 fill / neutral.50 text',
    ],
    themeChange: {
      status: 'proposed',
      note: 'Selected/idle styling for the themed segmented toggle — see ToggleButtonGroup.',
    },
    rationale:
      'A single toggle within a themed ToggleButtonGroup; its look comes from the group theme, not the call site.',
  },
  Switch: { approach: 'pipe', tokens: ['color primary'] },

  // Pipe-through: the pill radius + no-elevation it once proposed shipped to the
  // llama theme, so the prototype override was deleted in an inspect-mode sweep.
  // It's just the theme now — nothing to add. History is in the changelog.
  Button: {
    demo: { route: '/karehero-app/onboarding' },
    approach: 'pipe',
    tokens: ['textTransform none', 'no elevation', 'sizeLarge: minHeight 56'],
    changelog: [
      {
        date: '2026-06',
        note: 'Pill radius (borderRadius 9999) shipped to the llama theme; redundant prototype override deleted during sweep — Button is back to pipe-through.',
        status: 'merged',
      },
    ],
  },

  // ── Wrapped components: a reusable component owns the styling ──────────────
  ToggleButtonGroup: {
    approach: 'theme',
    tokens: [
      "opt in with className='segmented'",
      'sunflower.400 track, 4px inset + 4px gap',
      'pill radius, no dividers/borders',
      'selected: coral.700 fill, neutral.50 text',
      'idle: text.primary; hover sunflower.500',
    ],
    themeChange: {
      status: 'proposed',
      note: "Themed the MUI ToggleButtonGroup to carry the KareHero segmented control (sunflower track + coral selected pill) on real toggles, with the selected pill inset so the warm track and warm pill read as separate. Opt in with className='segmented', matching the convention llama already uses for its MuiTabs segmented control. This replaced the bespoke SegmentedFilter wrapper — every call site now uses the themed group directly, so the segmented control is pipe-through-the-theme, not a custom component.",
    },
    rationale:
      'The themed MUI ToggleButtonGroup that reads as the KareHero segmented control. Opt-in, not default: production ships stock MUI toggles, so a plain group here has to render stock too or the prototypes stop being evidence about the real app — the care search toolbar (care type, List/Map) is the case that proved it. Replaced the SegmentedFilter wrapper, so the segmented control is now pipe-through-the-theme. Guard deselect at the call site: exclusive groups return null on a re-click.',
  },
  Onboarding: {
    demo: { route: '/karehero-app/onboarding' },
    approach: 'wrapped',
    tokens: [
      'marine progress bar',
      'limeGreen.200 selected option',
      'floral white surfaces',
    ],
    rationale:
      'Post-signup onboarding wizard (8 steps + conditional 5a). Built from the Onboarding.html wireframe in our DS; runs after sign-up, before Home.',
    changelog: [
      {
        date: '2026-06',
        note: 'Built onboarding flow from the Onboarding.html wireframe',
        status: 'pending',
      },
    ],
    file: 'front-end/designs/src/karehero-app/onboarding/Onboarding.tsx',
  },
  DocRow: {
    demo: { route: '/karehero-app/profile' },
    approach: 'wrapped',
    tokens: [
      'file-lines 20 primary.main',
      'ellipsis-vertical 18 neutral.500',
      'CircularProgress 20 while uploading',
    ],
    rationale:
      'A document row (Card + file icon + name + kebab) in the Profile tab and call summaries. Kebab opens the doc actions sheet; a spinner replaces it mid-upload.',
    changelog: [
      {
        date: '2026-06',
        note: 'Built for the Profile tab Docs list',
        status: 'pending',
      },
    ],
    file: 'front-end/designs/src/karehero-app/care/DocRow.tsx',
  },
  ChatActionRow: {
    demo: {
      route: '/tell-us-once/intake-profile-review?scene=chat-v2-carer-name',
    },
    approach: 'wrapped',
    tokens: [
      'offer: marineGreen.50 fill, marineGreen.100 border',
      'go: limeGreen.200 fill, primary.main border',
      'press: primary.main border and scale 0.99, 120ms — no hover on a phone',
      'optional Icon 16 primary.main, body1 label, radius.md',
    ],
    rationale:
      'The tappable row inside a chat: an offer under the question, or the way on out of it. On floral white with a neutral border it was the same surface as a bubble and the same surface as a card, so a row you could tap sat in the transcript looking like something that had been said — the lightest marine with a step darker around it reads as a control at a glance and still sits quietly. Wrapped rather than a variant because it carries the tone and the icon colour together, and it is the one object every chat in the app should use for this.',
    changelog: [
      {
        date: '2026-09',
        note: 'Press states: the border goes marine and the row gives a per cent under the thumb, since a phone has no hover',
        status: 'pending',
      },
      {
        date: '2026-09',
        note: 'Lifted out of ChatV2 so the intake and the pre-sign-up chat cannot drift into two teals',
        status: 'pending',
      },
    ],
    file: 'front-end/designs/src/components/wrapped/ChatActionRow.tsx',
  },
  Note: {
    demo: { route: '/tell-us-once/keni-journey' },
    approach: 'wrapped',
    tokens: [
      'header fill: limeGreen.500 | cherry.500 | sunflower.500',
      'subtitle1 600 title · body1 body',
      'background.paper body, divider border, 4px radius',
    ],
    rationale:
      'An annotation from the Annotations Figma file: a titled bar over a paragraph, for saying something to whoever is looking at a prototype. Chrome, never product — colour tells two notes apart rather than carrying a severity.',
    changelog: [
      {
        date: '2026-08',
        note: 'Ported from the Annotations Figma file; the file’s purple variant is the retired lavender, so sunflower stands in',
        status: 'pending',
      },
    ],
    file: 'front-end/designs/src/designs-dev/annotations/Note.tsx',
  },
  TinyHeader: {
    approach: 'wrapped',
    tokens: [
      'fill: limeGreen.500 | sunflower.500 | cherry.500 | coral.600 | marineGreen.700',
      'subtitle1 600, charcoal on light fills and white on dark',
      '4px radius',
    ],
    rationale:
      'The small end of the Annotations file’s header set: a filled bar naming a run of screens. Its Section and Subsection headers are 5865px canvas furniture and stayed in Figma.',
    changelog: [
      {
        date: '2026-08',
        note: 'Ported from the Annotations Figma file, five colours of its six — the sixth is the retired lavender',
        status: 'pending',
      },
    ],
    file: 'front-end/designs/src/designs-dev/annotations/TinyHeader.tsx',
  },
  ErrorState: {
    approach: 'wrapped',
    tokens: [
      'exclamation-triangle 32 error.main',
      'h2 (Lora) title',
      'body1 text.secondary',
      'primary lg Try again',
    ],
    rationale:
      'The generic in-phone load-failure state. One pattern for every screen that can fail to load; copy comes from the call site.',
    changelog: [
      {
        date: '2026-06',
        note: 'Built for the shortlist handover (load-failure gap)',
        status: 'pending',
      },
    ],
    file: 'front-end/designs/src/components/mui/ErrorState.tsx',
  },
  CareSecuredCta: {
    approach: 'wrapped',
    tokens: ['primary lg Button', 'success.main banner', 'check-circle 20'],
    rationale:
      'The family’s "care secured" declaration at the end of the shortlist pages — notifies the care expert and wraps up the search.',
    changelog: [
      {
        date: '2026-06',
        note: 'Built for mobile shortlist viewing (mini-PRD)',
        status: 'pending',
      },
    ],
    file: 'front-end/designs/src/karehero-app/care/shortlists/CareSecuredCta.tsx',
  },
  ProviderCard: {
    approach: 'wrapped',
    tokens: [
      'status Chip: success | limeGreen | warmOrange | default',
      'h4 name · h4 price + body2 /week',
      'small Chips for care types + rating',
      'neutral.100 note block',
      'opacity 0.6 when Unavailable',
    ],
    rationale:
      'A provider on a published shortlist as the care circle sees it. Status is read-only — only CAMs change it.',
    changelog: [
      {
        date: '2026-06',
        note: 'Built for mobile shortlist viewing (mini-PRD)',
        status: 'pending',
      },
    ],
    file: 'front-end/designs/src/karehero-app/care/shortlists/ProviderCard.tsx',
  },
  Navigation: {
    approach: 'wrapped',
    tokens: [
      'logo 30w',
      'h2 optional name',
      'bell 18 (IconButton)',
      'Avatar 36 neutral.400 / neutral.50',
      '1px bottom border neutral.200',
      'minHeight APP_HEADER_H (60)',
    ],
    rationale:
      'The root-page navigation bar on every primary tab destination (Home, Actions, Care …): logo + optional name on the left, an optional bell + account avatar on the right. The counterpart to AppBar — Navigation is the persistent root header (no back), AppBar is the contextual bar for pushed pages and flows. One component so every root header is identical; owns the avatar styling so it never lands as a per-instance sx.',
    changelog: [
      {
        date: '2026-06',
        note: 'Split the shell header in two: Navigation (root pages, this) and AppBar (pushed pages / flows). Navigation carries the former AppBar content (logo + name + bell + avatar).',
        status: 'pending',
      },
    ],
    file: 'front-end/designs/src/karehero-app/shell/Navigation.tsx',
  },
  AppBar: {
    approach: 'wrapped',
    tokens: [
      'back chevron (IconButton) or "Back" link',
      'h2 centred title',
      'body2 optional subtitle',
      'optional trailing action',
      '1px bottom divider (optional)',
      'minHeight APP_HEADER_H (60)',
    ],
    rationale:
      'The contextual app bar for pushed pages and flows: a back chevron with a centred title (and optional subtitle + trailing action), or a plain "Back" bar when there is no title. The counterpart to Navigation (the persistent root header). Consumed by StackPage, so every pushed header is identical and the same height as the root Navigation — the bar does not jump when you push from a root page into a sub-page.',
    changelog: [
      {
        date: '2026-06',
        note: 'Repurposed from the old root header into the pushed-page / flow bar; the header was extracted out of StackPage so StackPage now consumes it.',
        status: 'pending',
      },
    ],
    file: 'front-end/designs/src/karehero-app/shell/AppBar.tsx',
  },
  Body: {
    approach: 'wrapped',
    tokens: [
      'the screen body below the app bar (fills, only it scrolls)',
      'takes children; shell lays them out',
    ],
    rationale:
      'The body region of a screen, below the app bar — a reusable layout shell. The CAM control panel uses it as the row holding the sidebar and content area; the mobile shell has its own scrollable take on the same idea.',
    changelog: [
      {
        date: '2026-06',
        note: 'Moved into components/layout as a reusable layout shell',
        status: 'pending',
      },
    ],
    file: 'front-end/designs/src/components/layout/Body.tsx',
  },
  ShortlistList: {
    approach: 'wrapped',
    tokens: [
      'Stack spacing 2',
      'one Card per shortlist',
      'success Chip "n secured"',
    ],
    rationale:
      'The list of published shortlist cards on the care search page: one Card per shortlist with a provider count + last-updated subtitle, and a secured-count chip once the family has secured anyone.',
    changelog: [
      {
        date: '2026-06',
        note: 'Named layer extracted from ShortlistHome',
        status: 'pending',
      },
    ],
    file: 'front-end/designs/src/karehero-app/care/shortlists/ShortlistList.tsx',
  },
  ShortlistSummaryCard: {
    approach: 'wrapped',
    tokens: [
      'Card (wrapped)',
      'titleVariant h3 + subtitle count · updated',
      'success Chip "n secured" when any secured',
      'navigational: passes onClick, so Card shows its chevron-right',
    ],
    rationale:
      'One published shortlist as a card in the member care search list. Built on the wrapped Card; derives the provider-count subtitle and the secured-count chip from the shortlist. (Distinct from the CAM-side ShortlistCard, which is a provider card within a CAM shortlist.)',
    changelog: [
      {
        date: '2026-06',
        note: 'Extracted the per-shortlist card out of ShortlistList',
        status: 'pending',
      },
    ],
    file: 'front-end/designs/src/karehero-app/care/shortlists/ShortlistSummaryCard.tsx',
  },
  CareSearchContent: {
    approach: 'wrapped',
    tokens: ['Stack (spacing 2)', 'composes ShortlistList'],
    rationale:
      'The shortlists page content under Body: a Stack holding the list of published shortlists. The page header came off — the tab already names the section, so a title saying it again was the third naming of the same thing before the first card.',
    changelog: [
      {
        date: '2026-06',
        note: 'Extracted the care search content Stack into a named layer',
        status: 'pending',
      },
    ],
    file: 'front-end/designs/src/karehero-app/care/shortlists/CareSearchContent.tsx',
  },
  ShortlistsEmptyState: {
    approach: 'wrapped',
    tokens: ['centred column', 'h3 (Lora) title', 'body1 text.secondary'],
    rationale:
      'The one empty message the shortlist surfaces share: before a care expert has published anything, and on a published list nobody has confirmed yet. Copy and placement are the caller’s; the type treatment is here.',
    changelog: [
      {
        date: '2026-06',
        note: 'Named layer extracted from ShortlistHome',
        status: 'pending',
      },
    ],
    file: 'front-end/designs/src/karehero-app/care/shortlists/ShortlistsEmptyState.tsx',
  },
  ActionCard: {
    approach: 'wrapped',
    tokens: ['composes Card + Checkbox + Typography + Avatar'],
    rationale: 'Composition - consume it, do not rebuild per screen.',
    file: 'front-end/designs/src/components/wrapped/ActionCard.tsx',
  },
  BottomSheet: {
    approach: 'wrapped',
    tokens: ['variant hug | full', 'top radius 20', '1px border'],
    changelog: [
      {
        date: '2026-06',
        note: 'hug/full variants; portals over the tab bar',
        status: 'pending',
      },
    ],
    file: 'front-end/designs/src/designs-dev/device/BottomSheet.tsx',
  },
  TabBar: {
    approach: 'wrapped',
    tokens: ['lime.200 bar', 'lime.500 sliding pill', 'olive/marine states'],
    changelog: [
      { date: '2026-06', note: 'Animated sliding pill', status: 'pending' },
    ],
    file: 'front-end/designs/src/designs-dev/device/TabBar.tsx',
  },
  ModalScreen: {
    approach: 'wrapped',
    tokens: ['280ms ease translateY', 'background.default surface'],
    rationale:
      'Full-screen modal that slides up from the bottom — PushScreen’s vertical twin. Presents a full page modally (e.g. the call summary from an action sheet) so the flow stays contained — no tab jumps.',
    changelog: [
      {
        date: '2026-06',
        note: 'Built for linked calls opened from action sheets',
        status: 'pending',
      },
    ],
    file: 'front-end/designs/src/designs-dev/device/ModalScreen.tsx',
  },
  PushScreen: {
    approach: 'wrapped',
    tokens: ['240ms ease translateX', 'background.default surface'],
    rationale:
      'Pushed stack pages (call summary, flows) slide in from the right — the BottomSheet pattern, horizontal — with the SignUpFlow edge-swipe to go back. One pop action shared by the back button and the gesture.',
    changelog: [
      {
        date: '2026-06',
        note: 'Built for the Care tab subscreens',
        status: 'pending',
      },
    ],
    file: 'front-end/designs/src/designs-dev/device/PushScreen.tsx',
  },
  PhoneFrame: {
    approach: 'wrapped',
    tokens: ['device mockup - not shipped'],
    file: 'front-end/designs/src/designs-dev/device/PhoneFrame.tsx',
  },
  StatusBar: {
    approach: 'wrapped',
    tokens: ['device chrome'],
    file: 'front-end/designs/src/designs-dev/device/StatusBar.tsx',
  },
  HomeIndicator: {
    approach: 'wrapped',
    tokens: ['47px bottom safe area'],
    file: 'front-end/designs/src/designs-dev/device/HomeIndicator.tsx',
  },
  MobileKeyboard: {
    approach: 'wrapped',
    tokens: ['decorative iOS keyboard'],
    file: 'front-end/designs/src/designs-dev/device/MobileKeyboard.tsx',
  },

  // ── CAM care search (desktop prototype, src/pages/CareSearch.tsx) ─────────
  ProviderCardContent: {
    approach: 'wrapped',
    tokens: ['h4 name · body1 address/phone', 'inlineChip + right slots'],
    rationale:
      'Content-only: the call site supplies the Card, so search results and shortlist items share typography with one card primitive.',
    file: 'front-end/designs/src/cam-admin/care-search/CareSearch.tsx',
  },
  StatusChipMenu: {
    approach: 'wrapped',
    tokens: ['badge 20px', 'status colours: limeGreen/success/warmOrange'],
    rationale:
      'Tappable provider status with the Dismissed escape in its menu; shared by full cards and basket rows.',
    file: 'front-end/designs/src/cam-admin/care-search/CareSearch.tsx',
  },
  ShortlistCard: {
    approach: 'wrapped',
    tokens: ['Card + ProviderCardContent + StatusChipMenu'],
    file: 'front-end/designs/src/cam-admin/care-search/CareSearch.tsx',
  },
  DismissedCard: {
    approach: 'wrapped',
    tokens: ['error badge', 'reason + restore'],
    file: 'front-end/designs/src/cam-admin/care-search/CareSearch.tsx',
  },
  AddToShortlistSplitButton: {
    approach: 'wrapped',
    tokens: ['outlined small ButtonGroup'],
    file: 'front-end/designs/src/cam-admin/care-search/CareSearch.tsx',
  },
  NoteItem: {
    approach: 'wrapped',
    tokens: ['neutral.100 bubble', 'own notes editable'],
    file: 'front-end/designs/src/cam-admin/care-search/CareSearch.tsx',
  },
  ShortlistPane: {
    approach: 'wrapped',
    tokens: ['340 basket / 420 full', 'neutral.50 · constant shell'],
    file: 'front-end/designs/src/cam-admin/care-search/CareSearch.tsx',
  },
  SearchPane: {
    approach: 'wrapped',
    tokens: ['xsmall search + filter grid', 'results list / map'],
    file: 'front-end/designs/src/cam-admin/care-search/CareSearch.tsx',
  },
  CareSearchFilters: {
    approach: 'wrapped',
    tokens: [
      'xsmall rounded search field + magnifying-glass adornment',
      'Clear all link when filters are active',
      'auto-fill grid of filter Selects (care type, rating)',
    ],
    rationale:
      'The CAM care search filter bar: the search field and the grid of filter selects, wrapped as one named layer. Composes TextField + Select; no per-instance styling beyond the search width/radius.',
    file: 'front-end/designs/src/cam-admin/care-search/CareSearch.tsx',
  },
  MapView: {
    approach: 'wrapped',
    tokens: ['mock map · illustrative pins'],
    file: 'front-end/designs/src/cam-admin/care-search/CareSearch.tsx',
  },
  CareDetail: {
    approach: 'wrapped',
    tokens: ['provider overlay with global notes'],
    file: 'front-end/designs/src/cam-admin/care-search/CareSearch.tsx',
  },
  NotesPanel: {
    approach: 'wrapped',
    tokens: ['CAM-only global notes'],
    file: 'front-end/designs/src/cam-admin/care-search/CareSearch.tsx',
  },
  CardMedia: { approach: 'pipe', tokens: ['media slot; placeholder fill'] },
  CardContent: { approach: 'pipe', tokens: [] },
  CardActions: { approach: 'pipe', tokens: [] },
  ControlPanel: {
    approach: 'wrapped',
    tokens: ['care recipient control panel shell: header, tabs, tab panes'],
    file: 'front-end/designs/src/cam-admin/control-panel/ControlPanel.tsx',
  },
  Sidebar: {
    approach: 'wrapped',
    tokens: [
      'square Paper surface, gardenia bg, 248px rail',
      '1px right border neutral.200',
      'MenuList of nav destinations',
    ],
    rationale:
      'A fixed-width navigation rail: a square Paper surface (gardenia, 248px, right border) that takes its nav content as children. A reusable layout shell — used for the CAM admin left nav.',
    file: 'front-end/designs/src/components/layout/Sidebar.tsx',
  },
  Layout: {
    approach: 'wrapped',
    tokens: [
      'flex column, fills its row',
      'standard p 3 gutter',
      'content max-width 1200, centred',
      'overflow hidden (its child scrolls)',
    ],
    rationale:
      'The content area of a shell: a flex column that fills beside the sidebar and holds the active view, with its content capped at 1200 and centred so a wide monitor gets margin rather than wider tables. A reusable layout shell that takes children.',
    file: 'front-end/designs/src/components/layout/Layout.tsx',
  },
  NotificationsList: {
    approach: 'wrapped',
    tokens: [
      'Stack of recency chunks (Today / This week / Earlier)',
      'GroupLabel per chunk + a NotificationStack',
      'empty chunks skipped',
    ],
    rationale:
      'The CAM notifications feed: customer activity grouped into recency chunks. Reads the already-filtered items and lays them out chunk by chunk, each chunk a label + a NotificationStack.',
    file: 'front-end/designs/src/cam-admin/notifications/NotificationsList.tsx',
  },
  NotificationStack: {
    approach: 'wrapped',
    tokens: [
      'Stack (spacing 1.5) of NotificationRow',
      'owns type → icon/tint, the customer line, the envelope toggle',
    ],
    rationale:
      'The rows for one recency chunk: composes the shared NotificationRow, wiring each CamNotification to its icon/tint, "Caring for {customer}" line and read/unread envelope.',
    file: 'front-end/designs/src/cam-admin/notifications/NotificationStack.tsx',
  },
  ComputerFrame: {
    approach: 'wrapped',
    tokens: ['cherry laptop mockup · 1440×900 scaled - not shipped'],
    file: 'front-end/designs/src/designs-dev/device/ComputerFrame.tsx',
  },
};

// How each approach reads on a chip, and the colour that carries its meaning.
export const APPROACH_META: Record<
  DsApproach,
  { label: string; color: 'limeGreen' | 'cherry' | 'warmOrange' }
> = {
  pipe: { label: 'Pipe through MUI', color: 'limeGreen' },
  wrapped: { label: 'Wrapped component', color: 'cherry' },
  theme: { label: 'Theme change', color: 'warmOrange' },
};

// What the approach means for the engineer building it — shown with the
// build spec wherever a registry entry renders.
export const APPROACH_GUIDANCE: Record<DsApproach, string> = {
  pipe: 'Use the MUI component directly and pipe your content in. The theme already styles it. No wrapper; sx for layout only.',
  wrapped:
    'Import this component and pass content through its props. The styling lives inside it. Don’t restyle it at the call site.',
  theme:
    'Its look comes from a global theme change, not the component. Check the change’s status before relying on it.',
};

// Shown instead of the wrapped guidance for a feature component (a wrapped
// component whose file lives in a product area, per componentRole). It composes
// rather than owns novel styling, so the build note is about scope, not look.
export const FEATURE_GUIDANCE =
  'Feature component: composition scoped to one feature or screen. It owns its layout and composes design-system components + primitives (Box/Stack) — it isn’t a reusable DS primitive. Consume the DS components it’s built from; it graduates into the design system via its handover, not on its own.';

// Shown for a layout-role component (a wrapped component living in a layout/
// directory) — the reusable shell/structure of a screen.
export const LAYOUT_GUIDANCE =
  'Layout component: the reusable shell/structure of a screen (sidebar, body, content area). It owns its own layout via system props and composes whatever you pass as children — consume it to lay a screen out. Not feature-specific, and not a styled DS atom.';

// How a changelog status reads on a chip.
export const STATUS_META: Record<
  ChangeStatus,
  { label: string; color: 'warmOrange' | 'limeGreen' }
> = {
  pending: { label: 'Pending', color: 'warmOrange' },
  merged: { label: 'Merged to main', color: 'limeGreen' },
};

export type CompiledChange = ChangeEntry & {
  component: string;
  demo?: DsEntry['demo'];
};

// Flattens every component's changelog into one list, newest first. The aggregate
// Changelog page and the per-component Spec both read from here, so they stay in sync.
export const collectChangelog = (): CompiledChange[] =>
  Object.entries(REGISTRY)
    .flatMap(([component, entry]) =>
      (entry.changelog ?? []).map((c) => ({
        ...c,
        component,
        demo: entry.demo,
      })),
    )
    .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));

// Layout / structural containers. The inspector treats these as boundaries you can
// hover *inside* (to select their contents). Every other component is selected as a
// whole unit - you don't drill into wrappers or pipe components.
export const LAYOUTS = new Set([
  'PhoneFrame',
  'BottomSheet',
  'TabBar',
  'StatusBar',
  'HomeIndicator',
  'MobileKeyboard',
  'PushScreen',
  'ModalScreen',
  // CAM desktop: pane-level containers you hover inside, not select whole.
  'ControlPanel',
  'ComputerFrame',
  'Sidebar',
  'Body',
  'Layout',
  'NotificationsList',
  'NotificationStack',
  'SearchPane',
  'CareSearchFilters',
  'ShortlistPane',
  'CareDetail',
  'NotesPanel',
  'MapView',
  // Cards are containers too — hovering their contents selects the pieces;
  // hovering the card's own chrome selects the card.
  'Card',
]);

// Registry component names the inspector resolves to.
export const INSPECTABLE = new Set(Object.keys(REGISTRY));
